import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import {
    default as getWacInfoForOneDriveJsApi,
    OfficeBundle,
} from 'owa-attachment-wac/lib/services/getWacInfoForOneDriveJsApi';
import getWacUrlForAttachmentId from 'owa-attachment-wac/lib/utils/getWacUrlForAttachmentId';
import { getGuid } from 'owa-guid';
import loc from 'owa-localize';
import { errorGetWacUrl } from 'owa-locstrings/lib/strings/errorgetwacurl.locstring.json';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    lazySetSaveStatusText,
    lazySetWacReadOnly,
    lazySxSDataModule,
    LoadedInfo,
    SxSViewState,
    WacPreviewState,
} from 'owa-sxsdata';
import { trace } from 'owa-trace';
import { getOrigin } from 'owa-url';
import * as React from 'react';
import { parse as parseUrl } from 'url';
import { IApplication } from 'word.boot/src/IApplication';
import {
    IApplicationBootstrapper,
    IApplicationInitializeDiagnostics,
    IApplicationInitializeParameters,
    IHostInitializeDiagnostics,
} from 'word.boot/src/IApplicationBootstrapper';
import { IApplicationCustomSettings } from 'word.boot/src/IApplicationCustomSettings';
import { canLoadWacUsingJsApi } from '../utils/canLoadWacUsingJsApi';
import { SxSCustomData } from '../utils/combineCustomData';
import { fileNameToOfficeOnlineApp } from '../utils/fileExtensionToOfficeOnlineApp';
import { postToWac } from 'owa-attachment-wac/lib/utils/iframeUtilities';
import { useWindowEvent } from 'owa-react-hooks/lib/useWindowEvent';

export interface WacDocumentPreviewProps {
    className?: string;
    viewState: SxSViewState;
    onLoad: (loadedInfo?: LoadedInfo) => void;
    onError: (message: string, error?: Error) => void;
    getExtraCustomDataForDatapoint: () => SxSCustomData;
    onFocusCommandBar: () => void;
    onFocusRightHandPane: () => Promise<void>;
}

interface JsApiAppSwitchParams {
    NewMode: string;
    Editable: boolean;
}

interface WacMessage {
    MessageId: string;
    SendTime: number;
    Values: any;
}

const styles = require('./WacDocumentPreview.scss');
const classNames = require('classnames/bind').bind(styles);

const JS_API_VIEW_MODE = 'view';

export default observer(function WacDocumentPreview(props: WacDocumentPreviewProps) {
    useWindowEvent('message', async (evt: MessageEvent) => {
        const messageData = evt ? evt.data : null;
        trace.info(`WacDocumentPreview: ReceivePostMessage ${messageData}`);
        // check to see if the event is coming from WAC iframe
        // the case of the URL in the post message event may be different from the one we had
        // convert to lower case before matching them
        if (
            !currentWacUrl.current ||
            currentWacUrl.current.toLowerCase().indexOf(evt.origin.toLowerCase()) !== 0
        ) {
            return;
        }
        let message: WacMessage = null;
        try {
            message = JSON.parse(evt.data) as WacMessage;
        } catch (ex) {
            trace.warn('Unable to parse post message data: ' + evt.data);
        }
        if (!message) {
            return;
        }
        // Handle the messages we want
        if (message.MessageId === 'App_LoadingStatus') {
            if (message.Values?.DocumentLoadedTime) {
                const loadedInfo: LoadedInfo = {
                    loadedTime: message.Values.DocumentLoadedTime,
                };
                props.onLoad(loadedInfo);
            } else {
                props.onLoad();
            }
            let fetchStart = 0;
            let redirectCount = 0;
            try {
                fetchStart = window.performance.timing.fetchStart;
                redirectCount = window.performance.navigation.redirectCount;
            } catch (ex) {
                trace.warn('FetchStart or RedirectCount is not supported by the browser.');
            }
            // WAC expects to receive a post message of performance data
            // https://wopi.readthedocs.io/en/latest/scenarios/postmessage.html?highlight=host_perftiming
            message.MessageId = 'Host_PerfTiming';
            message.SendTime = Date.now();
            message.Values = {
                Click: wacPreviewState.current.clickTime,
                Iframe: iframeCreationTime.current,
                HostFrameFetchStart: fetchStart,
                RedirectCount: redirectCount,
            };
            postMessage(message, evt);
        } else if (message.MessageId === 'UI_SaveStatus') {
            const saveStatusText = message.Values.StatusText;
            const setSaveStatusText = await lazySetSaveStatusText.import();
            setSaveStatusText(props.viewState, saveStatusText);
            if (!loggedWacSavingEvent.current) {
                loggedWacSavingEvent.current = true;
                const extraCustomData = props.getExtraCustomDataForDatapoint
                    ? props.getExtraCustomDataForDatapoint()
                    : null;
                logUsage(
                    'SxSWacDocumentSaveEventReceived',
                    {
                        ...extraCustomData?.customData,
                    },
                    {
                        cosmosOnlyData:
                            extraCustomData && JSON.stringify(extraCustomData.cosmosOnlyData),
                    }
                );
            }
        } else if (message.MessageId === 'App_RefreshSessionInfo') {
            let tokens = null;
            try {
                const openUrl: string = wacPreviewState.current.jsApiInfo.openUrl;
                if (openUrl == null) {
                    throw new Error('openUrl is null');
                }
                const apiResponse = await getWacInfoForOneDriveJsApi(
                    AttachmentDataProviderType.OneDrivePro,
                    openUrl,
                    true /* isEdit, jsAPI always in edit mode */
                );
                tokens = [
                    {
                        Name: 'AccessToken',
                        Expiry: apiResponse.openWith.wac.accessTokenExpiry,
                        Value: apiResponse.openWith.wac.accessToken,
                    },
                    {
                        Name: 'FileGetUrl',
                        Expiry: apiResponse.openWith.wac.accessTokenExpiry,
                        Value: apiResponse.openWith.wac.fileGetUrl,
                    },
                ];
            } catch (error) {
                trace.warn(
                    `Something went wrong while calling getWacInfoForOneDriveJsApi when refreshing tokens: ${error}`
                );
                tokens = [
                    {
                        Name: 'Unknown',
                        Errors: error,
                    },
                ];
            }
            message.MessageId = 'Host_RefreshSessionInfo';
            message.SendTime = Date.now();
            message.Values = {
                Tokens: tokens,
            };
            postMessage(message, evt);
        } else if (
            message.MessageId === 'App_DocReboot' &&
            !canLoadWacUsingJsApi(wacPreviewState.current)
        ) {
            // Refresh legacy OWA viewer for GDPR compliance
            await reloadLegacyWac();
        } else if (message.MessageId === 'App_AccessibilityLoopComplete') {
            if (props.viewState.hideRightPane || message.Values?.Direction === 'backward') {
                props.onFocusCommandBar();
            } else {
                props.onFocusRightHandPane();
            }
        }
    });
    React.useEffect(() => {
        return () => {
            disposeWopiApplication();
        };
    }, []);
    React.useEffect(() => {
        if (wacPreviewState.current.wacUrl) {
            if (parseUrl(wacPreviewState.current.wacUrl)?.protocol !== 'https:') {
                const error = new Error('wacUrl does not have valid https scheme');
                props.onError(loc(errorGetWacUrl), error);
                return;
            }
            if (currentWacUrl.current !== wacPreviewState.current.wacUrl) {
                trace.info(
                    `WacDocumentPreview: Opening WAC for ${wacPreviewState.current.fileName}`
                );
                if (canLoadWacUsingJsApi(wacPreviewState.current)) {
                    // Load the WAC using the jsApi approach.
                    loadWacViaJsApi();
                } else {
                    // Load the WAC the old-fashioned way.
                    createIframeIfNeeded();
                    postToWac(
                        iframeElement.current,
                        wacPreviewState.current.wacUrl,
                        wacPreviewState.current.accessToken,
                        wacPreviewState.current.accessTokenTtl,
                        wacPreviewState.current.additionalParameters,
                        wacPreviewState.current.deprecatedTokenAndParameters,
                        wacPreviewState.current.isCloudy
                    );
                }
                currentWacUrl.current = wacPreviewState.current.wacUrl;
                loggedWacSavingEvent.current = false;
            }
        } else if (iframeElement.current) {
            iframeElement.current.src = 'about:blank';
            currentWacUrl.current = null;
        }
    });
    const containerDiv = React.useRef<HTMLDivElement>();
    const iframeElement = React.useRef<HTMLIFrameElement>();
    const currentWacUrl = React.useRef<string>();
    const iframeCreationTime = React.useRef<number>();
    const wopiApplication = React.useRef<IApplication>();
    const wacPreviewState = React.useRef<WacPreviewState>(
        props.viewState.previewPane as WacPreviewState
    );
    const loggedWacSavingEvent = React.useRef<boolean>();
    const createIframeIfNeeded = (): void => {
        if (!iframeElement.current) {
            iframeCreationTime.current = Date.now();
            const newIframeElement = document.createElement('iframe');
            containerDiv.current.appendChild(newIframeElement);
            iframeElement.current = newIframeElement;
        }
    };
    const loadWacViaJsApi = async (): Promise<void> => {
        const wopiSrcUrl = wacPreviewState.current.jsApiInfo.wopiSrcUrl;
        const startOfQuery = wacPreviewState.current.wacUrl.indexOf('?');
        const queryDelimiter = startOfQuery === -1 ? '?' : '&';
        const uihParameter = wacPreviewState.current.isCloudy ? '&uih=owa' : '';
        const wacUrl = `${wacPreviewState.current.wacUrl}${queryDelimiter}uiEmbed=1${uihParameter}&EXO_FlightID=jsapi`;

        // For now we'll use the same random GUID for both the container ID and the session ID.
        // It might be helpful to use the 'Exchange Session Id' value from GetWacAttachmentInfo
        // as the HostSessionId in the WAC API. The Exchange session ID is not currently sent
        // to the client, but it could be added easily.
        const randomGuid = getGuid();
        // This doesn't need to be the same as the session ID - any unique string will work.
        // It just needs to match the ID that we put into wopiInitParam.Container.
        containerDiv.current.id = randomGuid;
        const officeOnlineAppInfo = fileNameToOfficeOnlineApp(wacPreviewState.current.fileName);
        if (officeOnlineAppInfo == null) {
            const error = new Error('Not supported file: ' + wacPreviewState.current.fileName);
            props.onError(loc(errorGetWacUrl), error);
            return;
        }
        const viewInitializedTime = Date.now();
        await insertBootScript(wacPreviewState.current.jsApiInfo.bootScriptUrl);
        openWac(
            officeOnlineAppInfo.appName,
            wacUrl,
            wopiSrcUrl,
            wacPreviewState.current.jsApiInfo.accessToken,
            wacPreviewState.current.jsApiInfo.accessTokenExpiry,
            viewInitializedTime
        );
    };
    const postMessage = (message: WacMessage, evt: MessageEvent) => {
        let messageData: string = JSON.stringify(message);
        trace.info(`WacDocumentPreview: PostMessageToWac ${messageData}`);
        try {
            if (wacPreviewState.current.jsApiInfo && !iframeElement.current) {
                // If we use JsApi, we need find iframe element from container
                iframeElement.current = containerDiv.current.getElementsByTagName('iframe')[0];
            }
            iframeElement.current.contentWindow.postMessage(messageData, evt.origin);
        } catch (ex) {
            trace.warn('PostMessage to WAC iframe resulted in an exception: ' + ex);
        }
    };
    const reloadLegacyWac = async () => {
        trace.info(
            `WacDocumentPreview: Reloading Legacy WAC for ${wacPreviewState.current.fileName}`
        );
        currentWacUrl.current = null;
        disposeWopiApplication();
        const sxsDataModule = await lazySxSDataModule.import();
        const sxsViewState: SxSViewState = props.viewState;
        const rebootTime: number = new Date().getTime();
        const optionalParameters = {
            downloadUrl: wacPreviewState.current.downloadUrl,
            downloadShouldOpenNewTab: wacPreviewState.current.downloadShouldOpenNewTab,
            fileName: wacPreviewState.current.fileName,
            useHighContrastCommandBar: wacPreviewState.current.useHighContrastCommandBar,
            id: wacPreviewState.current.id,
        };
        sxsDataModule.setPreviewLoading(sxsViewState, '', optionalParameters);
        try {
            const isEdit = !wacPreviewState.current.readOnly;
            const wacUrl = await getWacUrlForAttachmentId(
                wacPreviewState.current.id,
                isEdit,
                'WacReboot' // action source
            );

            sxsDataModule.setWacPreviewState(
                sxsViewState,
                wacUrl.wacUrl, // wacUrl
                null, // JsApiInfo
                wacUrl.accessToken,
                wacUrl.accessTokenTtl,
                wacUrl.additionalParameters,
                wacUrl.deprecatedTokenAndParameters, // to be removed soon
                rebootTime,
                !isEdit, // readOnly info
                optionalParameters
            );
        } catch (error) {
            trace.warn(`Something went wrong while calling getWacUrlForAttachmentId: ${error}`);
            props.onError(loc(errorGetWacUrl), error);
        }
    };
    const reloadJsApiWac = async () => {
        trace.info(`WacDocumentPreview: Reloading WAC for ${wacPreviewState.current.fileName}`);
        currentWacUrl.current = null;
        disposeWopiApplication();
        const sxsDataModule = await lazySxSDataModule.import();
        const sxsViewState: SxSViewState = props.viewState;
        const rebootTime: number = new Date().getTime();
        const optionalParameters = {
            downloadUrl: wacPreviewState.current.downloadUrl,
            downloadShouldOpenNewTab: wacPreviewState.current.downloadShouldOpenNewTab,
            fileName: wacPreviewState.current.fileName,
            useHighContrastCommandBar: wacPreviewState.current.useHighContrastCommandBar,
            id: wacPreviewState.current.id,
        };
        sxsDataModule.setPreviewLoading(sxsViewState, '', optionalParameters);
        try {
            const openUrl: string = wacPreviewState.current.jsApiInfo.openUrl;
            if (openUrl == null) {
                throw new Error('openUrl is null');
            }
            const isEdit = !wacPreviewState.current.readOnly;
            const apiResponse = await getWacInfoForOneDriveJsApi(
                AttachmentDataProviderType.OneDrivePro,
                openUrl,
                isEdit
            );
            sxsDataModule.setWacPreviewState(
                sxsViewState,
                apiResponse.openWith.wac.applicationUrl, // wacUrl
                {
                    bootScriptUrl: apiResponse.openWith.wac.bootstrapperUrl,
                    wopiSrcUrl: apiResponse.openWith.wac.wopiSrc,
                    accessToken: apiResponse.openWith.wac.accessToken,
                    accessTokenExpiry: apiResponse.openWith.wac.accessTokenExpiry,
                    fileSize: wacPreviewState.current.jsApiInfo.fileSize,
                    fileGetUrl: apiResponse.openWith.wac.fileGetUrl,
                    bundleInfo: apiResponse.officeBundle,
                    fetchStartTime: apiResponse.fetchStartTime,
                    responseStartTime: apiResponse.responseStartTime,
                    openUrl: openUrl,
                    userId: apiResponse.openWith.wac.userId,
                    clientThrottlingProtection: apiResponse.openWith.wac.clientThrottlingProtection,
                    requestedCallThrottling: apiResponse.openWith.wac.requestedCallThrottling,
                },
                wacPreviewState.current.accessToken,
                wacPreviewState.current.accessTokenTtl,
                wacPreviewState.current.additionalParameters,
                wacPreviewState.current.deprecatedTokenAndParameters,
                rebootTime,
                !isEdit,
                optionalParameters
            );
        } catch (error) {
            trace.warn(`Something went wrong while calling getWacInfoForOneDriveJsApi: ${error}`);
            props.onError(loc(errorGetWacUrl), error);
        }
    };
    const onReceiveDocRebootMessage = (eventArgs: {}) => {
        // WAC cannot remove event handler properly. As a workaround, we need make sure
        // wopiApplication is valid before reloading.
        if (wopiApplication.current) {
            reloadJsApiWac();
        }
    };
    const disposeWopiApplication = () => {
        if (wopiApplication.current) {
            wopiApplication.current.RemoveEventHandler('DocReboot', onReceiveDocRebootMessage);
            wopiApplication.current.Dispose();
            wopiApplication.current = null;
        }
    };
    const openWac = (
        appName: string,
        wacUrl: string,
        wopiSrcUrl: string,
        accessToken: string,
        accessTokenExpiry: Date,
        viewInitializedTime: number
    ) => {
        const configuration = getUserConfiguration();
        const onSuccess = (api, appInitDiagnostics: IApplicationInitializeDiagnostics) => {
            wopiApplication.current = api;
            wopiApplication.current.RegisterEventHandler('DocReboot', onReceiveDocRebootMessage);

            let errorMessage: string;
            // Error code might be available to log
            if (appInitDiagnostics?.errorObject) {
                try {
                    errorMessage = JSON.stringify(appInitDiagnostics.errorObject);
                } catch {}
            }

            let loadedTime: number = 0;
            // Report load event.
            if (appInitDiagnostics?.perceivedLoadTime) {
                loadedTime = appInitDiagnostics.perceivedLoadTime.getTime();
            } else if (appInitDiagnostics?.documentLoadedTime) {
                loadedTime = appInitDiagnostics.documentLoadedTime;
            }

            const loadedInfo = {
                loadedTime: loadedTime,
                errorObject: errorMessage,
            };
            props.onLoad(loadedInfo);
        };
        const onFailed = (wacError, appInitDiagnostics) => {
            let errorMessage = 'Unable parse WAC error';
            try {
                errorMessage = JSON.stringify(wacError);
            } catch {}
            const error = new Error(errorMessage);
            props.onError(loc(errorGetWacUrl), error);
        };
        const onAppSwitch = async (params: JsApiAppSwitchParams) => {
            const { NewMode: appMode = '', Editable: editable = false } = params;
            const isReadOnly = appMode.toLowerCase() === JS_API_VIEW_MODE || !editable;
            const setWacReadOnly = await lazySetWacReadOnly.import();
            setWacReadOnly(props.viewState, isReadOnly);
        };
        let BundleInfo = null;
        if (wacPreviewState.current.jsApiInfo.bundleInfo) {
            const officeBundle = wacPreviewState.current.jsApiInfo.bundleInfo as OfficeBundle;
            BundleInfo = {
                MajorVersion: officeBundle.version,
                Url: officeBundle.url,
            };
        }
        const sessionContext = {
            pmo: getOrigin(),
            curl: 'about:blank',
            pmshare: false,
            breadcrumb: false,
        };
        const wopiInitParam: IApplicationInitializeParameters<
            IApplication,
            IApplicationCustomSettings
        > = {
            ApplicationCustomSettings: {},
            BootstrapperUrl: wacPreviewState.current.jsApiInfo.bootScriptUrl,
            Container: containerDiv.current,
            SessionInformation: {
                HostName: 'OWA',
                HostSessionId: wacPreviewState.current.jsApiInfo.owaSessionId
                    ? wacPreviewState.current.jsApiInfo.owaSessionId
                    : getGuid(),
                UserId: wacPreviewState.current.jsApiInfo.userId,
                UiLocale: configuration.SessionSettings.UserCulture,
                DisableChat: true,
                CookieCompliant: true,
                WdParams: { hh: '1', wdOwaUnifiedFlight: '1' },
                SessionContext: JSON.stringify(sessionContext),
                SupportsDocReboot: true,
                HideHeader: true,
                SupportsAccessibilityLoop: true,
            },
            ApplicationUrl: wacUrl,
            WopiPrecheckInfo: {
                FileName: wacPreviewState.current.fileName,
                FileSize: wacPreviewState.current.jsApiInfo.fileSize,
                FileGetUrl: wacPreviewState.current.jsApiInfo.fileGetUrl,
                BundleInfo: BundleInfo,
                clientThrottlingProtection:
                    wacPreviewState.current.jsApiInfo.clientThrottlingProtection,
                requestedCallThrottling: wacPreviewState.current.jsApiInfo.requestedCallThrottling,
            },
            Diagnostics: {
                entryPoint: wacPreviewState.current.jsApiInfo.sessionOrigin,
                userClickTime: wacPreviewState.current.clickTime,
                viewInitializedTime: viewInitializedTime,
                hostPageNavigationStartTime: wacPreviewState.current.clickTime,
                hostPageFetchStartTime: wacPreviewState.current.jsApiInfo.fetchStartTime,
                hostPageResponseStartTime: wacPreviewState.current.jsApiInfo.responseStartTime,
                officeBootstrapperStartTime: viewInitializedTime,
                officeBootstrapperEndTime: Date.now(),
            } as IHostInitializeDiagnostics,
            FnOnInitializeSuccess: onSuccess,
            FnOnInitializeFailure: onFailed,
            FnOnAppModeSwitched: onAppSwitch,
            WopiInfo: {
                WopiSrc: wopiSrcUrl,
                AccessToken: accessToken,
                AccessTokenExpiry: accessTokenExpiry,
            },
        };
        const appBootstrapper: IApplicationBootstrapper<IApplication, IApplicationCustomSettings> =
            Microsoft.Office[appName].Application;
        const wopiPendingApplication = appBootstrapper.InitializeWopiPending(wopiInitParam);
        if (wopiPendingApplication) {
            wopiPendingApplication.Complete(wopiInitParam.WopiInfo);
        }
    };
    return (
        <div
            ref={div => (containerDiv.current = div)}
            className={classNames(props.className, styles.iframeContainer)}
        />
    );
});

declare var Microsoft: any;

let bootScriptContainerDiv: HTMLDivElement;
const bootScriptStatus: { [bootScriptUrl: string]: boolean } = {};
const insertBootScript = async (bootScriptUrl: string): Promise<void> => {
    if (bootScriptStatus[bootScriptUrl]) {
        return;
    }

    if (!bootScriptContainerDiv) {
        const containerDiv = document.createElement('div');
        containerDiv.id = 'owa-wac-bootscript';
        bootScriptContainerDiv = containerDiv;
        document.body.appendChild(bootScriptContainerDiv);
    }

    await new Promise<void>(resolve => {
        const wacBootScriptElement = document.createElement('script');
        wacBootScriptElement.setAttribute('type', 'text/javascript');
        wacBootScriptElement.setAttribute('src', bootScriptUrl);
        wacBootScriptElement.addEventListener('load', () => {
            resolve();
        });

        bootScriptContainerDiv.appendChild(wacBootScriptElement);
        bootScriptStatus[bootScriptUrl] = true;
    });
};
